import React, { useState } from 'react';
import Icon from 'components/AppIcon';

const QuoteRequestForm = ({ onSubmit }) => {
  const [formData, setFormData] = useState({
    // Contact Information
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    
    // Service Details
    serviceType: '',
    poolType: '',
    poolSize: '',
    frequency: '',
    
    // Pool Specifications
    poolCondition: '',
    hasEquipment: '',
    additionalServices: [],
    
    // Scheduling
    preferredDate: '',
    preferredTime: '',
    urgency: 'normal',
    
    // Budget and Additional Info
    budgetRange: '',
    currency: 'CRC',
    additionalNotes: '',
    
    // Contact Preferences
    preferredContact: 'phone',
    bestTimeToCall: ''
  });

  const [errors, setErrors] = useState({});
  const [showAdvanced, setShowAdvanced] = useState(false);

  const serviceTypes = [
    { value: 'cleaning', label: 'Limpieza Regular' },
    { value: 'maintenance', label: 'Mantenimiento Completo' },
    { value: 'repair', label: 'Reparación de Equipos' },
    { value: 'chemical', label: 'Balanceo Químico' },
    { value: 'emergency', label: 'Servicio de Emergencia' },
    { value: 'consultation', label: 'Consultoría' }
  ];

  const poolTypes = [
    { value: 'residential', label: 'Residencial' },
    { value: 'commercial', label: 'Comercial' },
    { value: 'hotel', label: 'Hotel/Resort' },
    { value: 'community', label: 'Condominio' }
  ];

  const poolSizes = [
    { value: 'small', label: 'Pequeña (hasta 20m²)' },
    { value: 'medium', label: 'Mediana (20-40m²)' },
    { value: 'large', label: 'Grande (40-80m²)' },
    { value: 'xlarge', label: 'Extra Grande (80m²+)' }
  ];

  const frequencies = [
    { value: 'weekly', label: 'Semanal' },
    { value: 'biweekly', label: 'Quincenal' },
    { value: 'monthly', label: 'Mensual' },
    { value: 'oneTime', label: 'Una sola vez' },
    { value: 'asNeeded', label: 'Según necesidad' }
  ];

  const budgetRanges = {
    CRC: [
      { value: '25000-50000', label: '₡25,000 - ₡50,000' },
      { value: '50000-100000', label: '₡50,000 - ₡100,000' },
      { value: '100000-200000', label: '₡100,000 - ₡200,000' },
      { value: '200000+', label: '₡200,000+' }
    ],
    USD: [
      { value: '50-100', label: '$50 - $100' },
      { value: '100-200', label: '$100 - $200' },
      { value: '200-400', label: '$200 - $400' },
      { value: '400+', label: '$400+' }
    ]
  };

  const additionalServiceOptions = [
    { value: 'equipment-check', label: 'Revisión de Equipos' },
    { value: 'filter-cleaning', label: 'Limpieza de Filtros' },
    { value: 'tile-cleaning', label: 'Limpieza de Azulejos' },
    { value: 'deck-cleaning', label: 'Limpieza de Deck' },
    { value: 'water-testing', label: 'Análisis de Agua' },
    { value: 'chemical-supply', label: 'Suministro de Químicos' }
  ];

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (type === 'checkbox') {
      if (name === 'additionalServices') {
        setFormData(prev => ({
          ...prev,
          additionalServices: checked
            ? [...prev.additionalServices, value]
            : prev.additionalServices.filter(service => service !== value)
        }));
      } else {
        setFormData(prev => ({ ...prev, [name]: checked }));
      }
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Required fields validation
    if (!formData.firstName.trim()) newErrors.firstName = 'Nombre es requerido';
    if (!formData.lastName.trim()) newErrors.lastName = 'Apellido es requerido';
    if (!formData.email.trim()) {
      newErrors.email = 'Email es requerido';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email no es válido';
    }
    if (!formData.phone.trim()) {
      newErrors.phone = 'Teléfono es requerido';
    } else if (!/^\+?506\s?\d{4}-?\d{4}$/.test(formData.phone.replace(/\s/g, ''))) {
      newErrors.phone = 'Formato de teléfono inválido (+506 XXXX-XXXX)';
    }
    if (!formData.serviceType) newErrors.serviceType = 'Tipo de servicio es requerido';
    if (!formData.poolType) newErrors.poolType = 'Tipo de piscina es requerido';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (validateForm()) {
      onSubmit(formData);
    } else {
      // Scroll to first error
      const firstErrorField = document.querySelector('.border-error');
      if (firstErrorField) {
        firstErrorField.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  };

  return (
    <div className="bg-surface rounded-lg border border-border p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-heading font-bold text-text-primary mb-2">
          Solicitar Cotización
        </h2>
        <p className="text-text-secondary">
          Completa el formulario y te contactaremos con una cotización personalizada.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Contact Information */}
        <div>
          <h3 className="text-lg font-semibold text-text-primary mb-4 flex items-center">
            <Icon name="User" size={20} className="text-primary mr-2" />
            Información de Contacto
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="firstName" className="block text-sm font-medium text-text-primary mb-2">
                Nombre *
              </label>
              <input
                type="text"
                id="firstName"
                name="firstName"
                value={formData.firstName}
                onChange={handleInputChange}
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary nav-transition ${
                  errors.firstName ? 'border-error' : 'border-border'
                }`}
                placeholder="Tu nombre"
              />
              {errors.firstName && (
                <p className="text-error text-sm mt-1">{errors.firstName}</p>
              )}
            </div>

            <div>
              <label htmlFor="lastName" className="block text-sm font-medium text-text-primary mb-2">
                Apellido *
              </label>
              <input
                type="text"
                id="lastName"
                name="lastName"
                value={formData.lastName}
                onChange={handleInputChange}
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary nav-transition ${
                  errors.lastName ? 'border-error' : 'border-border'
                }`}
                placeholder="Tu apellido"
              />
              {errors.lastName && (
                <p className="text-error text-sm mt-1">{errors.lastName}</p>
              )}
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-text-primary mb-2">
                Correo Electrónico *
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary nav-transition ${
                  errors.email ? 'border-error' : 'border-border'
                }`}
                placeholder="tu@email.com"
              />
              {errors.email && (
                <p className="text-error text-sm mt-1">{errors.email}</p>
              )}
            </div>

            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-text-primary mb-2">
                Teléfono *
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary nav-transition ${
                  errors.phone ? 'border-error' : 'border-border'
                }`}
                placeholder="+506 8888-8888"
              />
              {errors.phone && (
                <p className="text-error text-sm mt-1">{errors.phone}</p>
              )}
            </div>
          </div>
        </div>

        {/* Service Details */}
        <div>
          <h3 className="text-lg font-semibold text-text-primary mb-4 flex items-center">
            <Icon name="Wrench" size={20} className="text-primary mr-2" />
            Detalles del Servicio
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="serviceType" className="block text-sm font-medium text-text-primary mb-2">
                Tipo de Servicio *
              </label>
              <select
                id="serviceType"
                name="serviceType"
                value={formData.serviceType}
                onChange={handleInputChange}
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary nav-transition ${
                  errors.serviceType ? 'border-error' : 'border-border'
                }`}
              >
                <option value="">Selecciona un servicio</option>
                {serviceTypes.map(service => (
                  <option key={service.value} value={service.value}>
                    {service.label}
                  </option>
                ))}
              </select>
              {errors.serviceType && (
                <p className="text-error text-sm mt-1">{errors.serviceType}</p>
              )}
            </div>

            <div>
              <label htmlFor="poolType" className="block text-sm font-medium text-text-primary mb-2">
                Tipo de Piscina *
              </label>
              <select
                id="poolType"
                name="poolType"
                value={formData.poolType}
                onChange={handleInputChange}
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary nav-transition ${
                  errors.poolType ? 'border-error' : 'border-border'
                }`}
              >
                <option value="">Selecciona el tipo</option>
                {poolTypes.map(type => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
              {errors.poolType && (
                <p className="text-error text-sm mt-1">{errors.poolType}</p>
              )}
            </div>

            <div>
              <label htmlFor="poolSize" className="block text-sm font-medium text-text-primary mb-2">
                Tamaño de la Piscina
              </label>
              <select
                id="poolSize"
                name="poolSize"
                value={formData.poolSize}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary nav-transition"
              >
                <option value="">Selecciona el tamaño</option>
                {poolSizes.map(size => (
                  <option key={size.value} value={size.value}>
                    {size.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="frequency" className="block text-sm font-medium text-text-primary mb-2">
                Frecuencia del Servicio
              </label>
              <select
                id="frequency"
                name="frequency"
                value={formData.frequency}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary nav-transition"
              >
                <option value="">Selecciona la frecuencia</option>
                {frequencies.map(freq => (
                  <option key={freq.value} value={freq.value}>
                    {freq.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Advanced Options Toggle */}
        <div className="border-t border-border pt-6">
          <button
            type="button"
            onClick={() => setShowAdvanced(!showAdvanced)}
            className="flex items-center space-x-2 text-primary hover:text-primary/80 nav-transition"
          >
            <Icon name={showAdvanced ? "ChevronUp" : "ChevronDown"} size={20} />
            <span className="font-medium">
              {showAdvanced ? 'Ocultar opciones avanzadas' : 'Mostrar opciones avanzadas'}
            </span>
          </button>
        </div>

        {/* Advanced Options */}
        {showAdvanced && (
          <div className="space-y-6">
            {/* Additional Services */}
            <div>
              <h4 className="text-md font-semibold text-text-primary mb-3">
                Servicios Adicionales
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {additionalServiceOptions.map(service => (
                  <label key={service.value} className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      name="additionalServices"
                      value={service.value}
                      checked={formData.additionalServices.includes(service.value)}
                      onChange={handleInputChange}
                      className="w-4 h-4 text-primary border-border rounded focus:ring-primary/20"
                    />
                    <span className="text-sm text-text-secondary">{service.label}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Budget Range */}
            <div>
              <h4 className="text-md font-semibold text-text-primary mb-3">
                Presupuesto Estimado
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label htmlFor="currency" className="block text-sm font-medium text-text-primary mb-2">
                    Moneda
                  </label>
                  <select
                    id="currency"
                    name="currency"
                    value={formData.currency}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary nav-transition"
                  >
                    <option value="CRC">Colones (₡)</option>
                    <option value="USD">Dólares ($)</option>
                  </select>
                </div>
                <div className="md:col-span-2">
                  <label htmlFor="budgetRange" className="block text-sm font-medium text-text-primary mb-2">
                    Rango de Presupuesto
                  </label>
                  <select
                    id="budgetRange"
                    name="budgetRange"
                    value={formData.budgetRange}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary nav-transition"
                  >
                    <option value="">Selecciona un rango</option>
                    {budgetRanges[formData.currency].map(range => (
                      <option key={range.value} value={range.value}>
                        {range.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Urgency Level */}
            <div>
              <h4 className="text-md font-semibold text-text-primary mb-3">
                Nivel de Urgencia
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {[
                  { value: 'low', label: 'Baja - Dentro de 2 semanas', color: 'success' },
                  { value: 'normal', label: 'Normal - Dentro de 1 semana', color: 'primary' },
                  { value: 'high', label: 'Alta - Dentro de 2-3 días', color: 'warning' },
                  { value: 'emergency', label: 'Emergencia - Inmediato', color: 'error' }
                ].map(urgency => (
                  <label key={urgency.value} className="flex items-center space-x-3">
                    <input
                      type="radio"
                      name="urgency"
                      value={urgency.value}
                      checked={formData.urgency === urgency.value}
                      onChange={handleInputChange}
                      className="w-4 h-4 text-primary border-border focus:ring-primary/20"
                    />
                    <span className="text-sm text-text-secondary">{urgency.label}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Additional Notes */}
        <div>
          <label htmlFor="additionalNotes" className="block text-sm font-medium text-text-primary mb-2">
            Notas Adicionales
          </label>
          <textarea
            id="additionalNotes"
            name="additionalNotes"
            value={formData.additionalNotes}
            onChange={handleInputChange}
            rows={4}
            className="w-full px-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary nav-transition resize-none"
            placeholder="Describe cualquier detalle específico sobre tu piscina o requerimientos especiales..."
          />
        </div>

        {/* Submit Button */}
        <div className="pt-6">
          <button
            type="submit"
            className="w-full bg-primary text-white py-4 px-6 rounded-lg font-semibold hover:bg-primary/90 nav-transition flex items-center justify-center space-x-2"
          >
            <Icon name="Send" size={20} />
            <span>Enviar Solicitud de Cotización</span>
          </button>
          <p className="text-xs text-text-secondary text-center mt-3">
            Al enviar este formulario, aceptas que nos pongamos en contacto contigo para proporcionarte una cotización.
          </p>
        </div>
      </form>
    </div>
  );
};

export default QuoteRequestForm;